import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/material.module';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { PlateValidatorService } from '../../../../services/validator/plate-validator.service';
import { InternationalPhoneInputComponent } from '../../../../shared/components/international-phone-input/international-phone-input.component';

@Component({
  selector: 'app-step-involved-vehicles',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslatePipe,
    InternationalPhoneInputComponent,
  ],
  templateUrl: './step-involved-vehicles.component.html',
  styleUrl: './step-involved-vehicles.component.scss',
})
export class StepInvolvedVehiclesComponent implements OnInit {
  @Input() form!: FormGroup;

  isConfirmed = false;
  phoneValidationStates: boolean[] = [];

  constructor(private fb: FormBuilder, private plateValidator: PlateValidatorService) {}

  ngOnInit() {
    if (!this.form.get('involvedVehicles')) {
      this.form.addControl('involvedVehicles', this.fb.array([this.createVehicleGroup()]));
    } else if (this.involvedVehiclesArray.length === 0) {
      this.involvedVehiclesArray.push(this.createVehicleGroup());
    }

    this.phoneValidationStates = this.involvedVehiclesArray.controls.map(() => true);

    const savedVehicles = this.involvedVehiclesArray.value;
    if (savedVehicles?.length > 0 && savedVehicles.some((v: any) => v.plate?.trim())) {
      this.isConfirmed = this.hasValidPlates();
    }
  }

  get involvedVehiclesArray(): FormArray {
    return this.form.get('involvedVehicles') as FormArray;
  }

  private createVehicleGroup(): FormGroup {
    return this.fb.group({
      plate: ['', [Validators.required, Validators.pattern(/^[A-Z]{2}[0-9]{3}[A-Z]{2}$/)]],
      nome: [''],
      cognome: [''],
      indirizzo: [''],
      telefono: [''],
    });
  }

  onPhoneValidation(index: number, isValid: boolean): void {
    this.phoneValidationStates[index] = isValid;
  }

  isPhoneValid(index: number): boolean {
    const telefono = this.involvedVehiclesArray.at(index)?.get('telefono')?.value;
    if (!telefono?.trim()) return true;
    return this.phoneValidationStates[index] ?? false;
  }

  hasCounterpartyData(index: number): boolean {
    const group = this.involvedVehiclesArray.at(index);
    if (!group) return false;
    const { nome, cognome, indirizzo, telefono } = group.value;
    return !!(nome?.trim() || cognome?.trim() || indirizzo?.trim() || telefono?.trim());
  }

  hasValidPlates(): boolean {
    const plates = this.involvedVehiclesArray.controls.map(c => c.get('plate')?.value || '');
    return this.plateValidator.areAllPlatesValid(plates) && this.plateValidator.hasAtLeastOneValidPlate(plates);
  }

  confirmVehicles(): void {
    this.isConfirmed = true;
    this.notifyWizard();
    this.closeCurrentAndOpenNext();
  }

  private closeCurrentAndOpenNext(): void {
    if (typeof (window as any).wizardComponent !== 'undefined') {
      (window as any).wizardComponent.closeInvolvedVehiclesAndOpenDynamics();
    }
  }

  private notifyWizard(): void {
    if (typeof (window as any).wizardComponent !== 'undefined') {
      (window as any).wizardComponent[this.isConfirmed ? 'onVehiclesConfirmed' : 'onVehiclesChanged']();
    }
  }

  isPlateValid(index: number): boolean {
    const plate = this.getPlateControl(index)?.value || '';
    return this.plateValidator.isValidPlate(plate);
  }

  canShowAddButton(index: number): boolean {
    if (!this.isPlateValid(index)) return false;
    if (this.hasDuplicates()) return false;
    return index === this.involvedVehiclesArray.length - 1;
  }

  hasDuplicates(): boolean {
    const wizard = (window as any).wizardComponent;
    return wizard ? wizard.checkDuplicatePlates() : false;
  }

  addVehicle(): void {
    this.involvedVehiclesArray.push(this.createVehicleGroup());
    this.phoneValidationStates.push(true);
    this.isConfirmed = false;
    this.notifyWizard();
  }

  canShowDeleteButton(_index: number): boolean {
    return true;
  }

  async removeVehicle(index: number): Promise<void> {
    const isLast = this.involvedVehiclesArray.length === 1;

    if (isLast) {
      // Se è l'ultima riga: svuota i campi invece di rimuovere la riga
      const group = this.involvedVehiclesArray.at(index);
      group?.patchValue({ plate: '', nome: '', cognome: '', indirizzo: '', telefono: '' });
      group?.get('plate')?.markAsTouched();
      this.isConfirmed = false;
      this.notifyWizard();
      return;
    }

    const plate = this.getPlateControl(index)?.value || '';

    if (!plate.trim()) {
      this.doRemoveVehicle(index);
      return;
    }

    const wizard = (window as any).wizardComponent;
    if (wizard) {
      const hasData = this.hasAssociatedData(plate, index, wizard);
      if (hasData) {
        const confirmed = await wizard.confirmVehicleRemoval(plate);
        if (confirmed) this.doRemoveVehicle(index);
      } else {
        this.doRemoveVehicle(index);
      }
    } else {
      this.doRemoveVehicle(index);
    }
  }

  private hasAssociatedData(plate: string, index: number, wizard: any): boolean {
    const vehicleLetter = wizard.vehicleLetters[index + 1];
    const dynamicsGroup = wizard.accidentDynamicsForm?.get('accidentDynamics');
    const circumstances = dynamicsGroup?.get(`vehicle${vehicleLetter}`)?.value || [];
    const photos = wizard.vehiclePhotos[vehicleLetter] || [];
    return circumstances.length > 0 || photos.length > 0 || this.hasCounterpartyData(index);
  }

  private doRemoveVehicle(index: number): void {
    this.involvedVehiclesArray.removeAt(index);
    this.phoneValidationStates.splice(index, 1);
    if (this.involvedVehiclesArray.length === 0) {
      this.involvedVehiclesArray.push(this.createVehicleGroup());
      this.phoneValidationStates = [true];
    }
    this.isConfirmed = false;
    this.notifyWizard();
  }

  getPlateControl(index: number) {
    return this.involvedVehiclesArray.at(index)?.get('plate');
  }

  onPlateChange(index: number, event: any): void {
    const value = event.target.value.toUpperCase();
    this.getPlateControl(index)?.setValue(value);
    this.isConfirmed = false;
    this.notifyWizard();
  }
}
